import { isNonEmptyString, isNotUndefined } from '@wistia/type-guards';
import type { ChangeEvent, ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { styled, css } from 'styled-components';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';
import { FormControlLabel } from '../../private/components/FormControlLabel';
import { useCheckboxGroup } from '../FormGroup/CheckboxGroup';

export type CheckboxSizeType = 'lg' | 'md' | 'sm';

const CheckIcon = () => (
  <svg
    fill="inherit"
    height="8"
    viewBox="0 0 10 8"
    width="10"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.47281 7.19303L0.377565 4.07999C0.191609 3.89297 0.191609 3.58973 0.377565 3.40268L1.05098 2.72537C1.23694 2.53833 1.53847 2.53833 1.72442 2.72537L3.80953 4.82245L8.27558 0.330729C8.46154 0.143704 8.76306 0.143704 8.94902 0.330729L9.62244 1.00804C9.8084 1.19506 9.8084 1.49831 9.62244 1.68535L4.14624 7.19305C3.96027 7.38008 3.65876 7.38008 3.47281 7.19303Z"
      fill="var(--wui-checkbox-icon-color)"
    />
  </svg>
);

const sizeSmall = css`
  --wui-checkbox-size: 14px;
  --wui-checkbox-icon-size: 7px;
`;

const sizeMedium = css`
  --wui-checkbox-size: 16px;
  --wui-checkbox-icon-size: 9px;
`;

const sizeLarge = css`
  --wui-checkbox-size: 20px;
  --wui-checkbox-icon-size: 11px;
`;

const getSizeCss = (size: CheckboxSizeType) => {
  if (size === 'sm') {
    return sizeSmall;
  }
  if (size === 'lg') {
    return sizeLarge;
  }
  return sizeMedium;
};

const StyledCheckboxWrapper = styled.div<{ disabled: boolean; $hideLabel: boolean }>`
  --wui-checkbox-background-color: var(--wui-color-bg-surface);
  --wui-checkbox-border-color: var(--wui-color-border-secondary);
  --wui-checkbox-icon-color: transparent;
  --wui-checkbox-outline-color: transparent;

  &:hover {
    --wui-checkbox-border-color: var(--wui-color-border-secondary-hover);
  }

  &:has(input:checked) {
    --wui-checkbox-background-color: var(--wui-color-bg-fill);
    --wui-checkbox-border-color: transparent;
    --wui-checkbox-icon-color: var(--wui-color-bg-fill-white);
  }

  &:has(input:disabled) {
    --wui-checkbox-background-color: var(--wui-color-bg-surface-disabled);
    --wui-checkbox-border-color: var(--wui-color-border-secondary);

    &:has(input:checked) {
      --wui-checkbox-icon-color: var(--wui-color-icon-disabled);
    }
  }

  &:has(input:focus-visible) {
    [data-wui-faux-input='true'] {
      --wui-checkbox-outline-color: var(--wui-color-focus-ring);
    }
  }

  display: flex;
  margin: 0;

  /* TODO this solves a problem but potentially causes and a11y issue */
  user-select: none;
`;

const StyledCheckboxInput = styled.div<{ $size: CheckboxSizeType; $disabled?: boolean }>`
  ${({ $size }) => getSizeCss($size)}
  margin: 0;
  border-radius: var(--wui-border-radius-01);
  display: grid;
  place-content: center;
  border-width: 1px;
  border-style: solid;
  border-color: var(--wui-checkbox-border-color);
  background-color: var(--wui-checkbox-background-color);
  outline-color: var(--wui-checkbox-outline-color);
  outline-width: 2px;
  outline-style: solid;
  outline-offset: 1px;
  transition: all var(--wui-motion-duration-01) var(--wui-motion-ease);
  margin-top: calc(calc(1lh - var(--wui-checkbox-size)) / 2);
  width: var(--wui-checkbox-size);
  height: var(--wui-checkbox-size);
  min-width: var(--wui-checkbox-size);
  min-height: var(--wui-checkbox-size);

  /* checkbox icon */
  svg {
    width: var(--wui-checkbox-icon-size);
    height: var(--wui-checkbox-icon-size);
  }
`;

const StyledHiddenCheckboxInput = styled.input`
  ${visuallyHiddenStyle}
  position: relative;
`;

export type CheckboxProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'hideLabel' | 'label' | 'onChange' | 'size'
> & {
  /**
   * Indicates the state of the checkbox
   */
  checked?: boolean;
  /**
   * Disables checkbox
   */
  disabled?: boolean;
  /**
   * Optional ID attribute
   */
  id?: string | undefined;
  /**
   * The label for the checkbox
   */
  label: ReactNode;
  /**
   * The description for the checkbox
   */
  description?: ReactNode | undefined;
  /**
   * The name of the checkbox. Submitted with its owning form as part of a name/value pair.
   */
  name?: string | undefined;
  /**
   * Callback function invoked when the value changes
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Indicates if the input is required
   */
  required?: boolean;
  /**
   * A predefined size for the checkbox
   */
  size?: CheckboxSizeType;
  /**
   * The value given as data when submitted with a `name`.
   */
  value?: string;
  /**
   * Whether to visually hide the label (it will still be available to screen readers)
   */
  hideLabel?: boolean;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      disabled = false,
      id,
      label,
      description,
      name,
      onChange,
      size = 'md',
      value,
      required = false,
      hideLabel = false,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const computedId = isNonEmptyString(id) ? id : `wistia-ui-checkbox-${generatedId}`;
    const checkboxGroupContext = useCheckboxGroup();

    // use context values if provided, otherwise fall back to the props
    const contextName = checkboxGroupContext?.name;
    const contextOnChange = checkboxGroupContext?.onChange;
    const contextValue = checkboxGroupContext?.value;
    const checkboxName = name ?? contextName;
    const handleOnChange = onChange ?? contextOnChange;
    const isChecked =
      isNotUndefined(value) && contextValue ? contextValue.includes(value) : checked;

    return (
      <StyledCheckboxWrapper
        $hideLabel={hideLabel}
        disabled={disabled}
      >
        <StyledHiddenCheckboxInput
          {...props}
          ref={ref}
          checked={isChecked}
          disabled={disabled}
          id={computedId}
          name={checkboxName}
          onChange={handleOnChange}
          required={required}
          type="checkbox"
          value={value}
        />
        <FormControlLabel
          controlSlot={
            <StyledCheckboxInput
              $disabled={disabled}
              $size={size}
              data-wui-faux-input="true"
            >
              <CheckIcon />
            </StyledCheckboxInput>
          }
          description={description}
          disabled={disabled}
          hideLabel={hideLabel}
          htmlFor={computedId}
          label={label}
        />
      </StyledCheckboxWrapper>
    );
  },
);

Checkbox.displayName = 'Checkbox';
